import React, { useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { PatientTrusteeResponseData } from 'kb-shared/graphql/types';
import { useMutation } from 'react-apollo';
import _mapValues from 'lodash/mapValues';
import Honeybadger from '@honeybadger-io/js';

import { FieldsRow, Icon, Text, InputsLabel } from './shared';
import TextInput, { Container } from 'components/TextInput/NewTextInput';
import { trustee } from 'assets/icons';
import { showErrorToast } from 'utilities/notificationUtils';
import { patientQuery } from 'components/Dashboard/ProfileWidget';

export const UPDATE_PATIENT_TRUSTEE = gql`
  mutation updatePatientTrustee(
    $name: String
    $id: String
    $phone: String
    $state: String
    $relationship: String
  ) {
    updatePatientTrustee(
      id: $id
      name: $name
      phone: $phone
      state: $state
      relationship: $relationship
    ) {
      patientTrustee {
        id
        phone
        name
        relationship
        state
      }
    }
  }
`;

export const DELETE_PATIENT_TRUSTEE = gql`
  mutation deletePatientTrustee($id: String!) {
    deletePatientTrustee(id: $id) {
      id
    }
  }
`;

type Props = {
  id?: string | null;
  name?: string | null;
  relationship?: string | null;
  phone?: string | null;
  state?: string | null;
  position?: number | null;
};

export default function TrusteesFields({ id, name, relationship, phone, state, position }: Props) {
  const [trusteeDetails, setTrusteeDetails] = useState({
    name,
    relationship,
    phone,
    state,
    id
  });
  const [updateTrusteeDetails] = useMutation<PatientTrusteeResponseData>(UPDATE_PATIENT_TRUSTEE, {
    refetchQueries: [{ query: patientQuery }]
  });
  const [deleteTrustee] = useMutation(DELETE_PATIENT_TRUSTEE, {
    refetchQueries: [{ query: patientQuery }]
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTrusteeDetails({
      ...trusteeDetails,
      [e.target.name]: e.target.value
    });
  };

  const shouldDelete = (details: typeof trusteeDetails) => {
    const { id, ...rest } = details;
    return !!id && Object.keys(rest).every(key => !(details as any)[key]);
  };

  const updateTrustee = async (event: React.FocusEvent<any>) => {
    if (event.target.value.length == 0 && trusteeDetails.id == null) {
      return;
    }

    try {
      if (shouldDelete(trusteeDetails)) {
        await deleteTrustee({ variables: { id: trusteeDetails.id } });

        const emptyTrustee = _mapValues(trusteeDetails, () => null);
        setTrusteeDetails(emptyTrustee);
      } else {
        const response = await updateTrusteeDetails({ variables: { ...trusteeDetails } });
        if (response.data) {
          const {
            __typename,
            ...patientTrustee
          } = response.data.updatePatientTrustee.patientTrustee;
          setTrusteeDetails(patientTrustee);
        }
      }
    } catch (res) {
      Honeybadger.notify(res);
      // @ts-ignore
      const errors = res.graphQLErrors.map(error => {
        return JSON.parse(error.message);
      });
      const errorCopy = errors[0].join(', ');
      return showErrorToast(errorCopy);
    }
  };

  return (
    <FieldsRow>
      <InputsLabel>
        <Icon src={trustee} />
        <Text>Trustee {position}</Text>
      </InputsLabel>
      <InputRow>
        <TextInput
          name="name"
          label="NAME OF TRUSTEE"
          value={trusteeDetails.name}
          onChange={handleChange}
          onBlur={updateTrustee}
        />
        <TextInput
          name="relationship"
          label="RELATIONSHIP"
          value={trusteeDetails.relationship}
          onChange={handleChange}
          onBlur={updateTrustee}
        />
      </InputRow>
      <InputRow>
        <TextInput
          name="phone"
          label="PHONE NUMBER"
          placeholder={'555-555-5555'}
          inputType="phone"
          value={trusteeDetails.phone}
          onChange={handleChange}
          onBlur={updateTrustee}
        />
        <TextInput
          name="state"
          label="STATE"
          value={trusteeDetails.state}
          onChange={handleChange}
          onBlur={updateTrustee}
        />
      </InputRow>
    </FieldsRow>
  );
}

const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;

  ${Container} {
    margin-right: 20px;
    width: 50%;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    flex-direction: column;

    ${Container} {
      width: 100%;
    }
  }
`;
